// ProductChatSeller.jsx
import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  BsArrowLeft,
  BsShop,
  BsSend,
  BsCart2,
  BsPaperclip,
  BsX,
} from "react-icons/bs";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import MyNavbar from "../../../../components/general/mynavbar/MyNavbar.js";
import AppContext from "../../../../contexts/AppContext.js";
import LoadingScreen from "../../../../components/general/animations/Animations.js";
import "../../../../assets/css/customer/components/ProductChatSeller.css";

const ProductChatSeller = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { baseUrl, baseUrlForFiles, incrementUnread, customerAuthToken } =
    useContext(AppContext);

  const product = location.state?.selectedProductForCustomer;

  const [loading, setLoading] = useState(true);
  const [chatId, setChatId] = useState(null);
  const [chatStatus, setChatStatus] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [chatFile, setChatFile] = useState(null);
  const [chatFilePreview, setChatFilePreview] = useState(null);

  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState("");
  const [quantity, setQuantity] = useState(1);

  const hasColors =
    product?.variant?.colors && product.variant.colors.trim() !== "";
  const hasSizes =
    product?.variant?.sizes && product.variant.sizes.trim() !== "";

  const viewChatMessages = (id) => {
    axios
      .get(`${baseUrl}/customer/conversation/getMessages`, {
        params: { conversation_id: id },
        headers: {
          Authorization: `Bearer ${customerAuthToken}`,
        },
      })
      .then((response) => {
        setChatMessages(response.data.messages);
        setChatStatus(response.data.conversation?.status || null);
        const chatEnd = document.getElementById("pcs-chat-end");
        if (chatEnd) {
          chatEnd.scrollIntoView({ behavior: "smooth" });
        }
      })
      .catch(() => {});
  };

  const startConversation = () => {
    const savedCustomer = JSON.parse(localStorage.getItem("loggedInCustomer"));

    const formdata = new FormData();
    formdata.append("chcustomer_id", savedCustomer.id);
    formdata.append("seller_id", product.seller_id);
    formdata.append("product_id", product.id);

    axios
      .post(`${baseUrl}/customer/conversation/startConversation`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${customerAuthToken}`,
        },
      })
      .then((response) => {
        const conversation = response.data.conversation;
        setChatId(conversation.id);
        setChatStatus(conversation.status);
        viewChatMessages(conversation.id);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const handleChatFileChange = (e) => {
    const file = e.target.files[0] || null;
    setChatFile(file);
    setChatFilePreview(file ? URL.createObjectURL(file) : null);
  };

  const removeChatFile = () => {
    setChatFile(null);
    setChatFilePreview(null);
  };

  const sendChatMessage = (e) => {
    e.preventDefault();

    if (!message.trim() && !chatFile) return;

    const formdata = new FormData();
    formdata.append("conversation_id", chatId);
    formdata.append("message", message);
    if (chatFile) {
      formdata.append("file_path", chatFile);
    }
    setMessage("");
    setChatFile(null);
    setChatFilePreview(null);

    axios
      .post(`${baseUrl}/customer/conversation/sendMessage`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${customerAuthToken}`,
        },
      })
      .then(() => {
        viewChatMessages(chatId);
      })
      .catch(() => {});
  };

  const handleQuantityChange = (type) => {
    if (type === "increment" && quantity < product.stock_quantity) {
      setQuantity(quantity + 1);
    } else if (type === "decrement" && quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const addToCart = () => {
    const savedCustomer = JSON.parse(localStorage.getItem("loggedInCustomer"));
    const price = product.price;
    const subtotal = price * quantity;

    const formdata = new FormData();
    formdata.append("chcustomer_id", savedCustomer.id);
    formdata.append("chproduct_id", product.id);
    formdata.append("price", price);
    formdata.append("color", selectedColor === null ? "" : selectedColor);
    formdata.append("size", selectedSize);
    formdata.append("subtotal", subtotal);
    formdata.append("quantity", quantity);
    formdata.append("isPurchased", 0);
    formdata.append("isCustomized", 1);

    axios
      .post(`${baseUrl}/customer/cart/addToCart`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${customerAuthToken}`,
        },
      })
      .then(() => {
        incrementUnread("cart");
        toast.success("Added to Cart");
      })
      .catch(() => {
        toast.error("Failed to add to cart");
      });
  };

  const handleAddToCart = () => {
    if (chatStatus === "declined") {
      toast.error("This customization was declined — can't add to cart.");
      return;
    }

    const savedCustomer = JSON.parse(localStorage.getItem("loggedInCustomer"));

    if (!savedCustomer?.id) {
      toast.error("Please login first!");
      navigate("/login");
      return;
    }

    if (hasSizes && !selectedSize) {
      toast.warning("Please select a size!");
      return;
    }

    const formdata = new FormData();
    formdata.append("chcustomer_id", savedCustomer.id);

    axios
      .post(`${baseUrl}/customer/cart/viewCart`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${customerAuthToken}`,
        },
      })
      .then((res) => {
        const existingCart = res.data.carts || [];
        const cartSellerId = existingCart[0]?.product?.seller_id;
        const newSellerId = product?.seller_id;

        if (existingCart.length > 0 && cartSellerId !== newSellerId) {
          Promise.all(
            existingCart.map((item) => {
              const deleteFormdata = new FormData();
              deleteFormdata.append("chcart_id", item.id);
              return axios.post(
                `${baseUrl}/customer/cart/deleteCart`,
                deleteFormdata,
              );
            }),
          ).then(() => addToCart());
        } else {
          addToCart();
        }
      })
      .catch(() => addToCart());
  };

  useEffect(() => {
    const savedCustomer = JSON.parse(localStorage.getItem("loggedInCustomer"));

    if (!product) {
      navigate("/categories");
      return;
    }
    if (!savedCustomer?.id) {
      navigate("/login");
      return;
    }

    startConversation();
  }, []);

  if (loading) return <LoadingScreen />;
  if (!product) return null;

  return (
    <>
      <MyNavbar />
      <ToastContainer />
      <div className="pcs-page">
        <div className="pcs-header">
          <button
            className="pcs-back-btn"
            onClick={() => navigate(-1)}
            aria-label="Back to product"
          >
            <BsArrowLeft />
          </button>
          <h1 className="pcs-title">Customize Your Product</h1>
        </div>

        <div className="pcs-grid">
          {/* LEFT — product card + variant selection */}
          <div className="pcs-product-card">
            <img
              src={`${baseUrlForFiles}/${product.images?.[0]?.image_path}`}
              alt={product.product_name}
              className="pcs-product-img"
            />
            <h3 className="pcs-product-name">{product.product_name}</h3>
            <p className="pcs-product-price">Rs {product.price}</p>

            {hasColors && (
              <div className="option-group">
                <label className="fw-semibold">Color:</label>
                <div className="color-options">
                  {product.variant.colors
                    .split(",")
                    .filter(Boolean)
                    .map((color, index) => (
                      <div
                        key={index}
                        className={`color-circle ${selectedColor === index ? "active" : ""}`}
                        style={{ backgroundColor: color }}
                        onClick={() =>
                          setSelectedColor(
                            selectedColor === index ? null : index,
                          )
                        }
                      />
                    ))}
                </div>
              </div>
            )}

            {hasSizes && (
              <div className="option-group">
                <label className="fw-semibold">Size: {selectedSize}</label>
                <div className="size-options">
                  {product.variant.sizes
                    .split(",")
                    .filter(Boolean)
                    .map((size) => (
                      <button
                        key={size}
                        className={`size-btn ${selectedSize === size ? "active" : ""}`}
                        onClick={() => setSelectedSize(size)}
                      >
                        {size}
                      </button>
                    ))}
                </div>
              </div>
            )}

            <div className="option-group">
              <label className="fw-semibold">Quantity:</label>
              <div className="quantity-row">
                <div className="quantity-box">
                  <button onClick={() => handleQuantityChange("decrement")}>
                    -
                  </button>
                  <span>{quantity}</span>
                  <button onClick={() => handleQuantityChange("increment")}>
                    +
                  </button>
                </div>
              </div>
            </div>

            <button
              className="btn-add-cart"
              onClick={handleAddToCart}
              disabled={chatStatus === "declined"}
              style={{
                width: "100%",
                opacity: chatStatus === "declined" ? 0.5 : 1,
                cursor: chatStatus === "declined" ? "not-allowed" : "pointer",
                background: chatStatus === "declined" ? "#e5e5e5" : "#FF8559",
                color: chatStatus === "declined" ? "#999" : "white",
              }}
            >
              <BsCart2 size={18} /> Add to Cart
            </button>

            {chatStatus === "declined" && (
              <div className="pcs-declined-note">
                <p>
                  Seller declined this customization, so it can't be added to
                  cart. You can order the simple product instead.
                </p>
                <button
                  type="button"
                  className="pcs-declined-link"
                  onClick={() => navigate(-1)}
                >
                  Go to Product Page
                </button>
              </div>
            )}
          </div>

          {/* RIGHT — chat panel */}
          <div className="pcs-chat-panel">
            <div className="pcs-chat-header">
              <div className="pcs-seller-avatar">
                {product.seller?.shop_image ? (
                  <img
                    src={`${baseUrlForFiles}/${product.seller.shop_image}`}
                    alt="Store"
                  />
                ) : (
                  <BsShop />
                )}
              </div>
              <div>
                <h4 className="pcs-seller-name">
                  {product.seller?.store_name || "Seller"}
                </h4>
                <p className="pcs-seller-subtitle">Seller</p>
              </div>
            </div>

            <div className="pcs-chat-messages">
              {chatMessages.length === 0 && (
                <p className="pcs-empty-state">
                  Start the conversation — ask about customization, sizing, or
                  anything else.
                </p>
              )}
              {chatMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`pcs-msg-row ${msg.sender === "chcustomer" ? "customer" : "seller"}`}
                >
                  <div
                    className={`pcs-msg-bubble ${msg.type === "file" ? "pcs-msg-bubble--file" : ""}`}
                  >
                    {msg.sender !== "chcustomer" && (
                      <div className="pcs-sender-label">Seller</div>
                    )}
                    {msg.type === "file" && msg.file_path && (
                      <img
                        src={`${baseUrlForFiles}/${msg.file_path}`}
                        alt="attachment"
                        className="pcs-msg-img"
                      />
                    )}
                    {msg.message && (
                      <p className="pcs-msg-text">{msg.message}</p>
                    )}
                    <span className="pcs-msg-time">
                      {new Date(msg.created_at).toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                </div>
              ))}
              <div id="pcs-chat-end" />
            </div>
            {chatStatus === "confirmed" && (
              <div className="pcs-status-banner confirmed">
                Customization confirmed
              </div>
            )}

            {chatStatus === "declined" && (
              <div className="pcs-status-banner declined">
                Customization declined
              </div>
            )}

            {chatFilePreview && (
              <div className="pcs-file-preview-row">
                <img
                  src={chatFilePreview}
                  alt="preview"
                  className="pcs-file-preview-img"
                />
                <span className="pcs-file-preview-name">{chatFile?.name}</span>
                <button
                  type="button"
                  className="pcs-file-preview-remove"
                  onClick={removeChatFile}
                >
                  <BsX size={14} />
                </button>
              </div>
            )}

            <form className="pcs-chat-input-area" onSubmit={sendChatMessage}>
              <label htmlFor="pcs-chat-file" className="pcs-attach-btn">
                <BsPaperclip size={16} />
              </label>
              <input
                id="pcs-chat-file"
                type="file"
                accept="image/*"
                onChange={handleChatFileChange}
                className="pcs-file-input"
              />
              <input
                type="text"
                className="pcs-message-input"
                placeholder="Type your message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              <button type="submit" className="pcs-send-btn" aria-label="Send">
                <BsSend size={16} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductChatSeller;
